import axios from 'axios';

const CHUNK_SIZE = 120;
const cache = new Map<string, string>();
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAP_API_KEY;

// Track the total number of characters translated
const translatedCharCount = ref(0);
const translationLanguage = ref('en');
const isTranslatedView = ref(false);
const wasLastTranslationFromApi = ref(false);

function resetTranslatedCharCount() {
	translatedCharCount.value = 0;
}

function setTranslatedView(val: boolean) {
	isTranslatedView.value = val;
}

function decodeHtmlEntities(str: string): string {
	const txt = document.createElement('textarea');
	txt.innerHTML = str;
	return txt.value;
}

async function _translateObject<T extends Record<string, string>>(
	obj: T,
	targetLang: string,
	isEnableDetection = false,
): Promise<{ result: T; error: Error | null }> {
	const keys = Object.keys(obj) as Array<keyof T>;
	const contents: string[] = [];
	const keyMap: (keyof T)[] = [];

	// Step 1: Gather all texts that are not cached for translation
	keys.forEach((k) => {
		const text = obj[k] ?? '';
		const cacheKey = `${text}__${targetLang}`;
		if (!cache.has(cacheKey)) {
			contents.push(text);
			keyMap.push(k);
		}
	});

	// Step 2: Detect language for all texts to translate (if enabled)
	let detectedLangs: (string | undefined)[] = [];
	if (isEnableDetection && contents.length > 0) {
		try {
			const detectRes = await axios.post(
				`https://translation.googleapis.com/language/translate/v2/detect?key=${GOOGLE_API_KEY}`,
				{ q: contents },
			);
			detectedLangs = detectRes.data.data.detections.map((dArr: any) => dArr[0].language);
		} catch (err: any) {
			// On error, fallback to translating all
			detectedLangs = contents.map(() => 'und');
		}
	}
	if (!isEnableDetection) {
		detectedLangs = contents.map(() => undefined);
	}

	// Step 3: Prepare translation batch for only those that need translation
	const toTranslate: string[] = [];
	const translateKeyMap: number[] = [];
	(keyMap || []).forEach((k, i) => {
		if (
			!isEnableDetection || // detection off: always translate
			(detectedLangs[i] && detectedLangs[i] !== targetLang && detectedLangs[i] !== 'und')
		) {
			toTranslate.push(contents[i]);
			translateKeyMap.push(i);
		} else if (detectedLangs[i] === targetLang) {
			// If already target language, cache as is
			cache.set(`${contents[i]}__${targetLang}`, contents[i]);
		}
	});

	// Step 4: Translate only if needed (in chunks, parallel)
	if (toTranslate.length > 0) {
		try {
			const chunkPromises: Promise<void>[] = [];
			for (let i = 0; i < toTranslate.length; i += CHUNK_SIZE) {
				const chunk = toTranslate.slice(i, i + CHUNK_SIZE);
				const chunkKeyMap = translateKeyMap.slice(i, i + CHUNK_SIZE);

				const promise = axios
					.post(`https://translation.googleapis.com/language/translate/v2?key=${GOOGLE_API_KEY}`, {
						q: chunk,
						target: targetLang,
					})
					.then((translationRes) => {
						const translatedBatch = translationRes.data.data.translations.map((t: any) => t.translatedText);
						translatedBatch.forEach((tr: string, idx: number) => {
							const origIdx = chunkKeyMap[idx];
							const original = contents[origIdx];
							const decoded = decodeHtmlEntities(tr);
							cache.set(`${original}__${targetLang}`, decoded);
						});
					});
				chunkPromises.push(promise);
			}
			await Promise.all(chunkPromises);
			wasLastTranslationFromApi.value = true;
		} catch (err: any) {
			console.error('Translation API error:', err);
			// On error, we'll just return the original text for any failed translations
			const result = { ...obj };
			return { result, error: err };
		}
	} else if (contents.length > 0) {
		wasLastTranslationFromApi.value = false;
	}

	// Step 5: Build result object as before
	const result = {} as T;
	keys.forEach((k) => {
		const text = obj[k];
		const cacheKey = `${text}__${targetLang}`;
		if (cache.has(cacheKey)) {
			result[k] = decodeHtmlEntities(cache.get(cacheKey)!) as T[keyof T];
		} else {
			result[k] = text as T[keyof T];
		}
	});
	return { result, error: null };
}

export function useTranslation() {
	const isTranslating = ref(false);
	const error = ref<Error | null>(null);

	async function translate<T extends Record<string, string>>(
		obj: T,
		isEnableDetection = false,
	): Promise<T> {
		const browserLang = getBrowserLanguage();
		translationLanguage.value = browserLang;
		isTranslating.value = true;
		error.value = null;

		// Count characters to be translated (sum of all string values in obj)
		const charCount = Object.values(obj).reduce((acc, val) => acc + (val?.length || 0), 0);

		try {
			const { result: translatedResult, error: translationError } = await _translateObject(
				obj,
				browserLang,
				isEnableDetection,
			);
			if (translationError) {
				error.value = translationError;
			} else {
				// Count characters translated
				translatedCharCount.value += charCount;
			}
			return translatedResult;
		} catch (err: any) {
			error.value = err;
			return obj;
		} finally {
			isTranslating.value = false;
		}
	}

	function trackTranslationSuccess(profileId: number | null) {
		if (!wasLastTranslationFromApi.value) {
			return;
		}
		const { trackEvent } = useGtm();
		trackEvent({
			event: GTM_EVENT_NAME.TRANSLATION_SUCCESS,
			user_id: profileId,
			content_language: translationLanguage.value,
			length: translatedCharCount.value,
			content_type: TRANSLATION_CONTENT_TYPE.HOME,
		});
		resetTranslatedCharCount();
	}

	return {
		isTranslatedView,
		isTranslating,
		error,
		translate,
		setTranslatedView,
		translatedCharCount,
		resetTranslatedCharCount,
		translationLanguage,
		wasLastTranslationFromApi,
		trackTranslationSuccess,
	};
}
